import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    navigate(res.user.role === "admin" ? "/admin" : "/dashboard");
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="login-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
        <div className="hidden md:block">
          <p className="overline mb-5">{t("login.aside.overline")}</p>
          <h1 className="font-serif font-light text-5xl text-[color:var(--ink)] leading-tight tracking-tight">
            {t("login.aside.title.a")}
            <br />
            <em className="text-[color:var(--accent)] not-italic">{t("login.aside.title.b")}</em>
          </h1>
          <p className="mt-6 text-[color:var(--ink-2)] leading-relaxed">
            {t("login.aside.body")}
          </p>
        </div>

        <div className="card-soft p-8 md:p-10">
          <h2 className="font-serif text-3xl text-[color:var(--ink)]">{t("login.title")}</h2>
          <p className="text-[color:var(--ink-2)] text-sm mt-1">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="overline block mb-2">{t("common.email")}</label>
              <input
                type="email"
                required
                className="input-warm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t("common.password")}</label>
              <input
                type="password"
                required
                className="input-warm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p className="text-sm text-[color:var(--accent-2)] bg-[color:var(--bg-2)] border border-[color:var(--line-soft)] rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center mt-2" data-testid="login-submit">
              {busy ? t("common.opening") : t("login.submit")}
            </button>
          </form>

          <p className="mt-2 text-xs text-[color:var(--ink-3)]">
            {t("common.or_return_to")}{" "}
            <Link to="/" className="underline underline-offset-4">{t("common.homepage")}</Link>.
          </p>

          {/* New-here options — sit just under the sign-in form */}
          <div className="mt-7 pt-6 border-t border-[color:var(--line-soft)]" data-testid="login-new-options">
            <p className="overline mb-4">{t("login.new_here")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Link
                to="/register?plan=existing"
                className="block text-left p-4 rounded-2xl border border-[color:var(--line-soft)] hover:border-[color:var(--accent)]/40 hover:bg-[color:var(--accent)]/5 transition-all"
                data-testid="login-option-existing"
              >
                <p className="overline text-[color:var(--accent)]">{t("login.option.existing.tag")}</p>
                <p className="font-serif text-lg text-[color:var(--ink)] mt-1.5">{t("login.option.existing.title")}</p>
                <p className="text-xs text-[color:var(--ink-2)] mt-1.5 leading-relaxed">
                  {t("login.option.existing.body")}
                </p>
              </Link>
              <Link
                to="/register?plan=online"
                className="block text-left p-4 rounded-2xl border border-[color:var(--line-soft)] hover:border-[color:var(--accent)]/40 hover:bg-[color:var(--accent)]/5 transition-all"
                data-testid="login-option-online"
              >
                <p className="overline text-[color:var(--accent)]">{t("login.option.online.tag")}</p>
                <p className="font-serif text-lg text-[color:var(--ink)] mt-1.5">{t("login.option.online.title")}</p>
                <p className="text-xs text-[color:var(--ink-2)] mt-1.5 leading-relaxed">
                  {t("login.option.online.body")}
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
